import React, { useState, useEffect, useRef } from "react";
import { Col, FormControl, Row, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import PubSub from "pubsub-js";
import { useNavigate } from "react-router-dom"; // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
import { validationMessages } from "../../utils/messages";
import { getFormattedTimestamp, getUserDetails } from "../../utils/helper";
import solisPowerApi from "../../api/solisPowerApi";
import { canCreateQuotation } from "../../utils/leadQuotationHelper"; // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow

// -------------------- INITIAL ERRORS --------------------
const initialErrors = {
  remarks: { message: validationMessages.remarks.required },
  file: { message: validationMessages.file.required },
};

const UserSiteVisitModal = (props) => {
  const googleMapAddress = useRef(null);
  const navigate = useNavigate(); // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
  const userInfo = getUserDetails();
  const { show, onHide, submitSiteVisit: submit, refreshList } = props;

  const [visitData, setVisitData] = useState(
    props.visitData ? props.visitData : {}
  );

  // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
  const [showCreateQuotationButton, setShowCreateQuotationButton] = useState(false);
  const [leadStatus, setLeadStatus] = useState(null);

  const [errors, setErrors] = useState(
    !props.visitData ? initialErrors : {}
  );
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [address, setAddress] = useState(props.visitData?.location);

  // -------------------- FILE UPLOAD --------------------
  const folderList =
    props?.visitData?.action === "check-in"
      ? [{ label: "Building photo", key: "BuildingPhoto" }]
      : [
        // { label: "Building photo", key: "BuildingPhoto" },
        { label: "Roof photos", key: "RoofPhotos" },
        { label: "Product location", key: "ProductLocation" },
        { label: "Layout photo", key: "LayoutPhoto" },
      ];

  const [filesByFolder, setFilesByFolder] = useState({
    BuildingPhoto: [],
    RoofPhotos: [],
    ProductLocation: [],
    LayoutPhoto: [],
  });

  const fileRefs = {
    BuildingPhoto: useRef(),
    RoofPhotos: useRef(),
    ProductLocation: useRef(),
    LayoutPhoto: useRef(),
  };

  const handleFileChange = (folderKey, files) => {
    setFilesByFolder((prev) => ({
      ...prev,
      [folderKey]: Array.from(files),
    }));

    setErrors((prev) => {
      delete prev.file;
      return { ...prev };
    });
  };

  const hasAnyFile = () =>
    Object.values(filesByFolder).some((f) => f.length > 0);

  // -------------------- FORM HANDLERS --------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "remarks" && !value && visitData?.action !== "check-out") {
      setErrors((prev) => ({
        ...prev,
        remarks: { message: validationMessages.remarks.required },
      }));
    } else if (name === "check_out_remark") {
      setErrors((prev) => {
        delete prev[name];
        return { ...prev };
      });
    } else {
      setErrors((prev) => {
        delete prev[name];
        return { ...prev };
      });
    }

    setVisitData((prev) => ({ ...prev, [name]: value }));
    setValidated(true);
  };

  const checkRequiredFields = () => {
    let isError = false;
    const isCheckOut = visitData?.action === "check-out";

    if (!isCheckOut && !visitData?.remarks) {
      setErrors((prev) => ({
        ...prev,
        remarks: { message: validationMessages.remarks.required },
      }));
      isError = true;
    }

    if (!hasAnyFile()) {
      setErrors((prev) => ({
        ...prev,
        file: { message: validationMessages.file.required },
      }));
      isError = true;
    }

    return isError;
  };

  // -------------------- SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkRequiredFields()) {
      setValidated(true);
      return;
    }

    const isCheckOut = visitData.action === "check-out";
    if (visitData.action === "check-in") {
      visitData.logindatetime = getFormattedTimestamp();
      visitData.loginlattitude = latitude;
      visitData.loginlongitude = longitude;
    } else {
      visitData.logoutdatetime = getFormattedTimestamp();
      visitData.logoutlattitude = latitude;
      visitData.logoutlongitude = longitude;
      visitData.visit_status = "Complete";
    }

    delete visitData.owner_name;
    delete visitData.lead_name;
    delete visitData.action;
    delete visitData.owner_ids;

    const formData = new FormData();

    Object.entries(filesByFolder).forEach(([folder, files]) => {
      files.forEach((file) => {
        formData.append(folder, file);
      });
    });

    setLoading(true);

    try {
      await solisPowerApi.createFile(visitData.parentid, formData);

      const result = await solisPowerApi.saveUserTracking(visitData);

      if (result?.success) {
        // After successful check-out, fetch lead status to check if quotation can be created
        if (isCheckOut && visitData.parentid) {
          try {
            const leadData = await solisPowerApi.fetchLeadById(visitData.parentid);
            if (leadData && leadData.lead_status) {
              setLeadStatus(leadData.lead_status);
              if (canCreateQuotation(leadData.lead_status)) {
                setShowCreateQuotationButton(true);
                refreshList && refreshList();
                PubSub.publish("RECORD_SAVED_TOAST", {
                  title: "Record Saved",
                  message: "Check-out completed successfully. You can now create a quotation.",
                });
                return;
              }
            }
          } catch (error) {
            console.error("Error fetching lead status:", error);
          }
        }
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Saved",
          message: "Record saved successfully",
        });
        submit();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -------------------- GOOGLE MAP --------------------
  useEffect(() => {
    if (!window.google || !address) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        setLatitude(location.lat());
        setLongitude(location.lng());

        const map = new window.google.maps.Map(
          googleMapAddress.current,
          {
            center: location,
            zoom: 14,
          }
        );

        const marker = new window.google.maps.Marker({
          map,
          position: location,
        });

        const info = new window.google.maps.InfoWindow({
          content: results[0].formatted_address,
        });

        marker.addListener("click", () => info.open(map, marker));
      }
    });
  }, [address]);

  useEffect(() => {
    setAddress(visitData.location);
  }, [visitData.location]);

  // Added on 2026-02-07 by Farhan Khan - Create Quotation Flow
  // Reset button visibility when modal opens/closes
  useEffect(() => {
    if (!show) {
      setShowCreateQuotationButton(false);
      setLeadStatus(null);
    }
  }, [show]);

  // -------------------- UI --------------------
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={handleSubmit} noValidate validated={validated}>
        <Modal.Header closeButton>
          <Modal.Title>
            Site Visit {visitData?.lead_name || ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* MAP */}
          <Row>
            <Col md={12}>
              <div
                ref={googleMapAddress}
                style={{ width: "100%", height: "200px" }}
              />
            </Col>
          </Row>

          <Row className="mt-2">
            <Col lg={6}>
              <Form.Label>{visitData?.action === "check-out" ? "Check In Remark" : "Check In Remark"}</Form.Label>
              <FormControl
                required={visitData?.action !== "check-out"}
                readOnly={visitData?.action === "check-out"}
                name="remarks"
                value={visitData?.remarks || ""}
                onChange={handleChange}
              />
              {errors?.remarks && (
                <div className="feedback-invalid">
                  {errors.remarks.message}
                </div>
              )}
            </Col>

            {visitData?.action === "check-out" && (
              <Col lg={6}>
                <Form.Label>Check Out Remark</Form.Label>
                <FormControl
                  name="check_out_remark"
                  value={visitData?.check_out_remark || ""}
                  onChange={handleChange}
                  placeholder="Enter check out remark"
                />
              </Col>
            )}

            <Col lg={6}>
              <Form.Label>Site Location</Form.Label>
              <FormControl
                name="location"
                value={visitData?.location || ""}
                onChange={handleChange}
              />
            </Col>

            {/* FILE UPLOAD */}
            <Col lg={12} className="mt-4">
              <h6>Upload Photos</h6>

              <div className="d-flex gap-3 flex-wrap">
                {folderList.map((folder, index) => {
                  const variants = [
                    "primary",
                    "warning",
                    "info",
                    "secondary",
                  ];

                  return (
                    <div key={folder.key}>
                      <Button
                        size="sm"
                        variant={visitData.action === "check-out" ? variants[index + 1] : variants[index]}
                        onClick={() =>
                          fileRefs[folder.key].current.click()
                        }
                      >
                        {folder.label}
                      </Button>

                      <Form.Control
                        type="file"
                        multiple
                        ref={fileRefs[folder.key]}
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleFileChange(
                            folder.key,
                            e.target.files
                          )
                        }
                      />

                      {filesByFolder[folder.key].length > 0 && (
                        <small className="text-success d-block mt-1">
                          {filesByFolder[folder.key].length} files
                        </small>
                      )}
                    </div>
                  );
                })}
              </div>

              {errors?.file && (
                <div className="feedback-invalid mt-2">
                  {errors.file.message}
                </div>
              )}
            </Col>

            {loading && (
              <Col lg={12} className="text-center mt-3">
                <Spinner />
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          {/* Added on 2026-02-07 by Farhan Khan - Create Quotation Flow */}
          {showCreateQuotationButton && visitData.parentid ? (
            <>
              <Button
                variant="success"
                onClick={() => {
                  navigate(`/quotations/e?lead_id=${visitData.parentid}`);
                  onHide();
                  submit(); // Close the modal and refresh the list
                }}
                style={{ marginRight: "8px" }}
              >
                <i className="fa fa-file-text me-1"></i> Create Quotation
              </Button>
              <Button variant="light" onClick={() => {
                onHide();
                submit(); // Close the modal and refresh the list
              }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button variant="success" type="submit" disabled={loading}>
                Submit
              </Button>
              <Button variant="light" onClick={onHide}>
                Close
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserSiteVisitModal;



// NEW
// import React, { useState, useEffect, useRef } from "react";
// import {
//   Col,
//   FormControl,
//   Row,
//   Spinner,
// } from "react-bootstrap";
// import Form from "react-bootstrap/Form";
// import Button from "react-bootstrap/Button";
// import Modal from "react-bootstrap/Modal";
// import PubSub from "pubsub-js";
// import { validationMessages } from "../../utils/messages";
// import { getFormattedTimestamp } from "../../utils/helper";
// import solisPowerApi from "../../api/solisPowerApi";

// // -------------------- INITIAL ERRORS --------------------
// const initialErrors = {
//   remarks: { message: validationMessages.remarks.required },
//   file: { message: validationMessages.file.required },
// };

// const UserSiteVisitModal = (props) => {
//   const googleMapAddress = useRef(null);
//   const { show, onHide, submitSiteVisit: submit } = props;

//   const [visitData, setVisitData] = useState(
//     props.visitData ? props.visitData : {}
//   );

//   const [errors, setErrors] = useState(
//     !props.visitData ? initialErrors : {}
//   );
//   const [validated, setValidated] = useState(false);
//   const [loading, setLoading] = useState(false);

//   const [latitude, setLatitude] = useState(null);
//   const [longitude, setLongitude] = useState(null);
//   const [address, setAddress] = useState(props.visitData?.location);

//   // -------------------- 🔥 FILE UPLOAD STATES --------------------
//   const folderList =
//     props?.visitData?.action === "check-in"
//       ? [{ label: "Building photo", key: "BuildingPhoto" }]
//       : [
//           { label: "Building photo", key: "BuildingPhoto" },
//           { label: "Roof photos", key: "RoofPhotos" },
//           { label: "Product location", key: "ProductLocation" },
//           { label: "Layout photo", key: "LayoutPhoto" },
//         ];

//   const [filesByFolder, setFilesByFolder] = useState({
//     BuildingPhoto: [],
//     RoofPhotos: [],
//     ProductLocation: [],
//     LayoutPhoto: [],
//   });

//   const fileRefs = {
//     BuildingPhoto: useRef(),
//     RoofPhotos: useRef(),
//     ProductLocation: useRef(),
//     LayoutPhoto: useRef(),
//   };

//   const handleFileChange = (folderKey, files) => {
//     setFilesByFolder((prev) => ({
//       ...prev,
//       [folderKey]: Array.from(files),
//     }));

//     setErrors((prev) => {
//       delete prev.file;
//       return { ...prev };
//     });
//   };

//   const hasAnyFile = () =>
//     Object.values(filesByFolder).some(
//       (files) => files.length > 0
//     );

//   // -------------------- FORM HANDLERS --------------------
//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "remarks" && !value) {
//       setErrors((prev) => ({
//         ...prev,
//         remarks: { message: validationMessages.remarks.required },
//       }));
//     } else {
//       setErrors((prev) => {
//         delete prev[name];
//         return { ...prev };
//       });
//     }

//     setVisitData((prev) => ({ ...prev, [name]: value }));
//     setValidated(true);
//   };

//   const checkRequiredFields = () => {
//     let isError = false;

//     if (!visitData?.remarks) {
//       setErrors((prev) => ({
//         ...prev,
//         remarks: { message: validationMessages.remarks.required },
//       }));
//       isError = true;
//     }

//     if (!hasAnyFile()) {
//       setErrors((prev) => ({
//         ...prev,
//         file: { message: validationMessages.file.required },
//       }));
//       isError = true;
//     }

//     return isError;
//   };

//   // -------------------- SUBMIT --------------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (checkRequiredFields()) {
//       setValidated(true);
//       return;
//     }

//     if (visitData.action === "check-in") {
//       visitData.logindatetime = getFormattedTimestamp();
//       visitData.loginlattitude = latitude;
//       visitData.loginlongitude = longitude;
//     } else {
//       visitData.logoutdatetime = getFormattedTimestamp();
//       visitData.logoutlattitude = latitude;
//       visitData.logoutlongitude = longitude;
//       visitData.visit_status = "Complete";
//     }

//     delete visitData.owner_name;
//     delete visitData.lead_name;
//     delete visitData.action;
//     delete visitData.owner_ids;

//     const formData = new FormData();

//     // 🔥 EXACT SAME AS FilesCreate
//     Object.entries(filesByFolder).forEach(([folder, files]) => {
//       files.forEach((file) => {
//         formData.append(folder, file);
//       });
//     });

//     setLoading(true);

//     try {
//       await solisPowerApi.createFile(
//         visitData.parentid,
//         formData
//       );

//       const result = await solisPowerApi.saveUserTracking(
//         visitData
//       );

//       if (result?.success) {
//         PubSub.publish("RECORD_SAVED_TOAST", {
//           title: "Record Saved",
//           message: "Record saved successfully",
//         });
//         submit();
//       }
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // -------------------- GOOGLE MAP --------------------
//   useEffect(() => {
//     setAddress(visitData.location);
//   }, [visitData.location]);

//   // -------------------- UI --------------------
//   return (
//     <Modal show={show} onHide={onHide} size="lg" centered>
//       <Form onSubmit={handleSubmit} noValidate validated={validated}>
//         <Modal.Header closeButton>
//           <Modal.Title>
//             Site Visit {visitData?.lead_name || ""}
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Row>
//             <Col lg={6} className="mt-2">
//               <Form.Label>Remarks</Form.Label>
//               <FormControl
//                 required
//                 name="remarks"
//                 value={visitData?.remarks || ""}
//                 onChange={handleChange}
//               />
//               {errors?.remarks && (
//                 <div className="feedback-invalid">
//                   {errors.remarks.message}
//                 </div>
//               )}
//             </Col>

//             <Col lg={6} className="mt-2">
//               <Form.Label>Site Location</Form.Label>
//               <FormControl
//                 name="location"
//                 value={visitData?.location || ""}
//                 onChange={handleChange}
//               />
//             </Col>

//             {/* 🔥 FILE UPLOAD BUTTONS */}
//             <Col lg={12} className="mt-4">
//               <h6>Upload Photos</h6>

//               <div className="d-flex gap-3 flex-wrap">
//                 {folderList.map((folder, index) => {
//                   const variants = [
//                     "primary",
//                     "warning",
//                     "info",
//                     "secondary",
//                   ];

//                   return (
//                     <div key={folder.key}>
//                       <Button
//                         size="sm"
//                         variant={variants[index]}
//                         onClick={() =>
//                           fileRefs[folder.key].current.click()
//                         }
//                       >
//                         {folder.label}
//                       </Button>

//                       <Form.Control
//                         type="file"
//                         multiple
//                         ref={fileRefs[folder.key]}
//                         style={{ display: "none" }}
//                         onChange={(e) =>
//                           handleFileChange(
//                             folder.key,
//                             e.target.files
//                           )
//                         }
//                       />

//                       {filesByFolder[folder.key].length > 0 && (
//                         <small className="text-success d-block mt-1">
//                           {filesByFolder[folder.key].length} files
//                         </small>
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>

//               {errors?.file && (
//                 <div className="feedback-invalid mt-2">
//                   {errors.file.message}
//                 </div>
//               )}
//             </Col>

//             {loading && (
//               <Col lg={12} className="text-center mt-3">
//                 <Spinner />
//               </Col>
//             )}
//           </Row>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button
//             variant="success"
//             type="submit"
//             disabled={loading}
//           >
//             Submit
//           </Button>
//           <Button variant="light" onClick={onHide}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default UserSiteVisitModal;

